import type { ServicePageData } from './types';

const glassManifestation: ServicePageData = {
  slug: 'glass-manifestation',
  eyebrow: 'Signage',
  title: 'Make your commercial glazing',
  titleAccent: 'safely visible.',
  intro:
    'Building regulations require large areas of transparent glazing that people could walk into to carry permanent, visible markings at two heights, contrasting with the background and visible from both sides in all lighting. Placing etched decals at strategic points on the glazing makes the glass clearly visible and prevents potential accidents.',
  heroImages: [
    '/home/glass-manifestation/hagley-etch.jpg',
    '/home/glass-manifestation/hagley-etch2.jpg',
    '/home/glass-manifestation/hodghill-etch.jpg',
    '/home/glass-manifestation/melting-pot-etch2.jpg',
    '/home/glass-manifestation/scouts-etch.jpg',
    '/home/signage/glass-manifestation.jpg',
    '/home/signage/etch-hodghill.jpg',
    '/home/glass-manifestation/hagley-etch.jpg',
    '/home/glass-manifestation/hagley-etch2.jpg',
    '/home/glass-manifestation/hodghill-etch.jpg',
    '/home/glass-manifestation/melting-pot-etch2.jpg',
    '/home/glass-manifestation/scouts-etch.jpg',
    '/home/signage/glass-manifestation.jpg',
    '/home/signage/etch-hodghill.jpg',
    '/home/glass-manifestation/hagley-etch.jpg',
    '/home/glass-manifestation/hagley-etch2.jpg',
    '/home/glass-manifestation/hodghill-etch.jpg',
    '/home/glass-manifestation/melting-pot-etch2.jpg',
    '/home/glass-manifestation/scouts-etch.jpg',
    '/home/signage/glass-manifestation.jpg',
    '/home/signage/etch-hodghill.jpg',
  ],

  useCases: [
    { title: 'Offices and meeting rooms', text: 'Ideal for glass room dividers to ensure safety.' },
    { title: 'Shopfronts and reception areas', text: 'A cost-effective way of turning a legal necessity into something stylish.' },
    { title: 'Schools and public buildings', text: 'Prevent potential accidents and injuries where people are likely to move around.' },
    { title: 'Sports and leisure facilities', text: 'Keep transparent glazing visible and compliant.' },
    { title: 'New builds and refurbishments', text: 'Ensure your building meets legal health and safety legislation.' },
  ],

  optionsHeading: 'Choose your style',
  optionsIntro: 'Glass manifestation is available in a range of styles to suit your branding and wayfinding requirements.',
  options: [
    {
      id: 'patterns',
      title: 'Patterns',
      lead: 'For subtle visual markers.',
      body: 'Decorative window film comes in 16 stock patterns ready to apply or chat to our design team who can come up with a custom design for you.',
      image: '/home/glass-manifestation/hagley-etch.jpg',
      specs: [
        { label: 'Design', value: '16 stock patterns ready to apply.' },
        { label: 'Customisation', value: 'Custom designs available.' },
        { label: 'Light', value: 'Standard etch allows up to 80% of natural light through.' },
      ],
    },
    {
      id: 'text',
      title: 'Text',
      lead: 'For helpful direction.',
      body: 'Double up on the functionality by adding notices to your glass, which also work as visual markers.',
      image: '/home/glass-manifestation/hodghill-etch.jpg',
      specs: [
        { label: 'Format', value: 'Supplied cut to size with custom cut-outs.' },
        { label: 'Surface', value: 'Suitable for any smooth glass surface.' },
      ],
    },
    {
      id: 'logos',
      title: 'Logos',
      lead: 'For added brand awareness.',
      body: 'The best way to make your required decals part of your environment, whilst still serving their purpose, is to incorporate your branding into the design.',
      image: '/home/glass-manifestation/scouts-etch.jpg',
      specs: [
        { label: 'Light', value: 'Standard etch allows up to 80% of natural light through.' },
        { label: 'Format', value: 'Supplied cut to size with custom cut-outs.' },
      ],
    },
    {
      id: 'full-coverage',
      title: 'Full coverage',
      lead: 'For maximum privacy and compliance.',
      body: 'Standard film allows up to 80% of natural light through while creating a permanent visual barrier.',
      image: '/home/glass-manifestation/melting-pot-etch2.jpg',
      specs: [
        { label: 'Compliance', value: 'Meets legal health and safety requirements.' },
        { label: 'Visibility', value: 'Contrasts with the background and is visible from both sides.' },
      ],
    },
  ],

  gallery: [
    '/home/glass-manifestation/hagley-etch.jpg',
    '/home/glass-manifestation/hagley-etch2.jpg',
    '/home/glass-manifestation/hodghill-etch.jpg',
    '/home/glass-manifestation/melting-pot-etch2.jpg',
    '/home/glass-manifestation/scouts-etch.jpg',
    '/home/signage/glass-manifestation.jpg',
    '/home/signage/etch-hodghill.jpg',
  ],

  steps: [
    {
      title: 'Arrange a free site visit',
      text: 'Our team will come to you to measure up your windows and take some photos, or send over your own measurements and photos and we\'ll take it from there.',
    },
    {
      title: 'Get a free quote and mockup',
      text: 'Our design team will put together a visual of how your windows could look; logo design available in house.',
    },
    {
      title: 'Approve your order',
      text: 'After you approve your mockup and quote, you\'re ready to book a slot with our production team.',
    },
    {
      title: 'Installation',
      text: 'Our professional and fully insured installation team will meet you on site to apply your window film.',
    },
  ],

  info: [
    {
      title: 'Professional installation',
      text: 'Our professional install team has years of experience applying flawless window films bubble-free throughout the Midlands.',
      bullets: [
        'Fully insured',
        'Bubble-free application',
        'Access equipment provided',
      ],
    },
    {
      title: 'Supply only',
      text: 'Want to give it a go yourself? We can supply window privacy film for you to apply yourself.',
      bullets: [
        'On the roll or cut to size',
        'Plain or with cut-outs',
        'Instructions provided',
      ],
    },
    {
      title: 'Care',
      text: 'Window film is very easy to look after and you can keep it clean with soapy water.',
      bullets: [
        'Wait at least 3 weeks before first clean',
        'Use soapy water on a soft cloth or paper towel',
        'No abrasive products',
        'No ammonia-based products',
        'Extra care around cut-out edges, no squeegee',
      ],
    },
  ],

  cta: {
    title: 'Get your free glass manifestation quote.',
    text: 'Tell us about your project and we will ensure your glazing meets legal requirements.',
    image: '/home/signage/glass-manifestation.jpg',
  },
};

export default glassManifestation;
